import { publishUIMessage } from '../shared/utils/notifications';
import { t } from '../utils/i18n';

class ApiError extends Error {
  constructor(message, statusCode) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

export class NotFoundError extends Error {
  constructor() {
    super('Resource not found');
    this.name = 'NotFoundError';
    this.statusCode = 404;
  }
}

export const keys = {
  trackingSessionMetadata: ['tracking_session_metadata'],
  authToken: ['merchant_account_token'],
  merchantAccountV2: (token) => ['merchant_account', 'v2', token],
  merchantV2: (merchantId, token) => ['merchant', 'v2', merchantId, token],
  merchantAppsV2: (merchantId, token) => [
    'merchant_app',
    'v2',
    merchantId,
    token,
  ],
  locations: () => ['locations'],
  merchantsSearch: (token) => ['merchantSearch', token],
  merchantOnboardingsV2: {
    fetchOnboardingSteps: (merchantId, token) => [
      'merchant_onboardings',
      'v2',
      merchantId,
      token,
    ],
    skipOnboardingStep: (merchantId, token) => [
      'merchant_onboardings',
      'v2',
      'skip_step',
      merchantId,
      token,
    ],
  },
  merchantPaymentSettingsV2: (merchantId, token) => [
    'merchant_payment_settings',
    merchantId,
    token,
  ],
  merchantPaymentSettingsV2b: (merchantId, token) => [
    'merchant_payment_settings_v2b',
    merchantId,
    token,
  ],
  contractV2: {
    findAll: (organizationId, token) => [
      'contractV2',
      'findAll',
      organizationId,
      token,
    ],
  },
  sms: {
    currentMonthUsage: (merchantId) => [
      'sms',
      'current_month_usage',
      merchantId,
    ],
  },
  subscription: {
    latest: (merchantId) => ['subscription', merchantId],
    portalSession: (merchantId) => ['subscriptionPortalSession', merchantId],
  },
  paypal: {
    initiateSetup: (merchantId) => ['paypal', 'initiateSetup', merchantId],
  },
  bookingConfigsV2: (merchantId, token) => [
    'bookingConfigs',
    'v2',
    merchantId,
    token,
  ],
};

function injectErrorMetadata(metadata) {
  return (error) => {
    error.metadata = metadata;
    return Promise.reject(error);
  };
}

/**
 * Waits for the authToken query if it is currently fetching, then checks again.
 * Only throws "Missing auth token!" after the authToken query is no longer loading/fetching.
 */
function authenticatedQuery(queryClient) {
  async function waitForAuthTokenIfFetching() {
    const queryKey = keys.authToken;

    // fast-path: already cached
    let token = queryClient.getQueryData(queryKey);
    if (token) return token;

    // If the authToken query is in-flight, wait for it to resolve, then re-check cache
    const query = queryClient.getQueryCache().find({ queryKey });
    const isFetching = query?.state?.fetchStatus === 'fetching';

    // TanStack Query stores the in-flight promise on the Query instance (v4/v5).
    // This avoids starting a second request and simply awaits the existing one.
    if (isFetching && query?.promise) {
      await query.promise;
      token = queryClient.getQueryData(queryKey);
      if (token) return token;
    }

    throw new Error('Missing auth token!');
  }

  return (configSetupFn) =>
    (...args) => {
      const withAuth = async (fn) => {
        const token = await waitForAuthTokenIfFetching();
        return fn(token);
      };

      return configSetupFn(withAuth, ...args);
    };
}

function retryDelayFn(attemptIndex) {
  return Math.min(1000 * 2 ** attemptIndex, 30000);
}

function retryUnless404(retryCount) {
  return (failureCount, error) => {
    if (error instanceof NotFoundError) {
      return false;
    }
    return failureCount < retryCount;
  };
}

async function handleApiResponse(response) {
  if (response.status === 404) {
    throw new NotFoundError();
  }

  const errorResponse = response.status >= 400;
  const responseData = await response.json();

  if (errorResponse) {
    throw new ApiError(JSON.stringify(responseData), response.status);
  }

  return responseData;
}

export default function createQueryConfig(queryClient, apiClient) {
  return {
    authToken: () => {
      const queryKey = keys.authToken;

      function fetchToken() {
        return apiClient.authV2
          .getOrCreateAccessToken()
          .then(String)
          .catch(injectErrorMetadata({ queryKey }));
      }

      return {
        queryKey,
        queryFn: fetchToken,
        gcTime: Infinity,
      };
    },

    subscription: authenticatedQuery(queryClient)(
      (withAuth, params, options = {}) => {
        const queryKey = keys.subscription.latest(params?.merchantId);
        const enabledFromCaller = options.enabled ?? true;

        async function fetchLatestSubscription() {
          return withAuth((token) =>
            apiClient.subscriptionV3
              .fetchLatestSubscription(params?.merchantId, token)
              .then(handleApiResponse)
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchLatestSubscription,
          gcTime: Infinity,
          retry: retryUnless404(3),
          retryDelay: retryDelayFn,
          enabled: enabledFromCaller && Boolean(params?.merchantId),
        };
      }
    ),

    subscriptionPortalSession: authenticatedQuery(queryClient)(
      (withAuth, params) => {
        const queryKey = keys.subscription.portalSession(params?.merchantId);

        async function fetchPortalSession() {
          return withAuth((token) =>
            apiClient.subscriptionV3
              .fetchPortalSession(params?.merchantId, token)
              .then(handleApiResponse)
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchPortalSession,
          gcTime: Infinity,
          retry: retryUnless404(3),
          retryDelay: retryDelayFn,
          enabled: Boolean(params?.merchantId),
        };
      }
    ),

    locations: () => {
      const queryKey = keys.locations();

      function fetchLocations() {
        return apiClient
          .getLocations()
          .catch(injectErrorMetadata({ queryKey }));
      }

      return {
        queryKey,
        queryFn: fetchLocations,
        gcTime: Infinity,
      };
    },

    downPayments: {
      setupDownPayments: authenticatedQuery(queryClient)((withAuth, params) => {
        const mutationKey = ['down_payments', params?.merchantId];

        async function setupDownPayments({ percentage, type }) {
          return withAuth((token) =>
            apiClient.downPayments
              .setupDownPayments(
                params?.merchantId,
                { percentage, type },
                token
              )
              .then(handleApiResponse)
              .catch(
                injectErrorMetadata({
                  mutationKey,
                  token,
                  merchantId: params?.merchantId,
                })
              )
          );
        }

        return {
          mutationKey,
          mutationFn: setupDownPayments,
          gcTime: Infinity,
          onSuccess: () =>
            publishUIMessage(
              'success',
              t('notifications.down-payment-configurations.success-message') ||
                ''
            ),
          onError: () =>
            publishUIMessage(
              'error',
              t('notifications.down-payment-configurations.error-message') || ''
            ),
        };
      }),

      disableDownPayments: authenticatedQuery(queryClient)(
        (withAuth, params) => {
          const mutationKey = ['down_payments', params?.merchantId];

          async function disableDownPayments() {
            return withAuth((token) =>
              apiClient.downPayments
                .disableDownPayments(params?.merchantId, token)
                .then(handleApiResponse)
                .catch(
                  injectErrorMetadata({
                    mutationKey,
                    token,
                    merchantId: params?.merchantId,
                  })
                )
            );
          }

          return {
            mutationKey,
            mutationFn: disableDownPayments,
            gcTime: Infinity,
            onSuccess: () =>
              publishUIMessage(
                'success',
                t(
                  'notifications.down-payment-configurations.success-message'
                ) || ''
              ),
            onError: () =>
              publishUIMessage(
                'error',
                t('notifications.down-payment-configurations.error-message') ||
                  ''
              ),
          };
        }
      ),
    },

    smsCurrentMonthUsage: (merchantId) => {
      const queryKey = keys.sms.currentMonthUsage(merchantId);
      const now = new Date();

      function fetchSmsCurrentMonthUsage() {
        return apiClient.smsUsage.findAll({
          filter: {
            month: now.getMonth() + 1,
            year: now.getFullYear(),
            merchant_id: merchantId,
          },
        });
      }

      return {
        queryKey,
        queryFn: fetchSmsCurrentMonthUsage,
        gcTime: Infinity,
        retry: retryUnless404(3),
        retryDelay: retryDelayFn,
      };
    },

    merchantAccountV2: authenticatedQuery(queryClient)((withAuth) => {
      const tokenForKey = queryClient.getQueryData(keys.authToken) || 'loading';
      const queryKey = keys.merchantAccountV2(String(tokenForKey));

      async function fetchMerchantAccount() {
        return withAuth((token) =>
          apiClient.merchantAccountV2
            .me({
              fields: {
                merchant_account: ['employee'],
                organizations: ['key_account_contract', 'test', 'name'],
              },
              include: ['employee', 'organization'],
            })
            .catch(injectErrorMetadata({ queryKey, token }))
        );
      }

      return {
        queryKey,
        queryFn: fetchMerchantAccount,
      };
    }),

    merchantV2: authenticatedQuery(queryClient)((withAuth, merchantId) => {
      const tokenForKey = queryClient.getQueryData(keys.authToken) || 'loading';
      const queryKey = keys.merchantV2(merchantId, String(tokenForKey));

      async function fetchMerchant() {
        const merchants = [
          'feature_toggles',
          'name',
          'organization',
          'created_at',
          'currency',
          'source',
          'ui_visibility',
          'category',
          'pixel_id',
          'slug',
          'phone',
        ];

        const organizations = [
          'id',
          'trial_ends_on',
          'trial_days_left',
          'self_sign_up',
          'test',
          'package_code',
          'whitelabel_slug',
        ];

        return withAuth((token) =>
          apiClient.merchantV2
            .find(merchantId, {
              include: ['organization'],
              fields: { organizations, merchants },
            })
            .catch(injectErrorMetadata({ merchantId, queryKey, token }))
        );
      }

      return {
        queryKey,
        queryFn: fetchMerchant,
      };
    }),

    merchantAppsV2: authenticatedQuery(queryClient)((withAuth, merchantId) => {
      const tokenForKey = queryClient.getQueryData(keys.authToken) || 'loading';
      const queryKey = keys.merchantAppsV2(merchantId, String(tokenForKey));

      async function fetchSessionPermissions() {
        return withAuth((token) =>
          apiClient.merchantAppsV2
            .find(merchantId)
            .catch(injectErrorMetadata({ merchantId, queryKey, token }))
        );
      }

      return {
        queryKey,
        queryFn: fetchSessionPermissions,
      };
    }),

    merchantsSearch: authenticatedQuery(queryClient)((withAuth) => {
      const tokenForKey = queryClient.getQueryData(keys.authToken) || 'loading';
      const queryKey = keys.merchantsSearch(String(tokenForKey));

      async function searchMerchant() {
        return withAuth((token) =>
          apiClient.algolia
            .search({
              keyFilter: [apiClient.algolia.MERCHANTS],
              options: {
                attributesToRetrieve: ['name', 'objectID'],
              },
            })
            .catch(injectErrorMetadata({ queryKey, token }))
        );
      }

      return {
        queryKey,
        queryFn: searchMerchant,
      };
    }),

    merchantOnboardingsV2: {
      fetchOnboardingSteps: authenticatedQuery(queryClient)(
        (withAuth, params) => {
          const tokenForKey =
            queryClient.getQueryData(keys.authToken) || 'loading';
          const queryKey = keys.merchantOnboardingsV2.fetchOnboardingSteps(
            params.merchantId,
            String(tokenForKey)
          );

          async function fetchMerchantOnboardingSteps() {
            return withAuth((token) =>
              apiClient.merchantOnboardingsV2
                .fetchOnboardingSteps({
                  merchantId: params.merchantId,
                  params: {
                    locale: params.locale || 'en',
                  },
                })
                .catch(injectErrorMetadata({ queryKey, token }))
            );
          }

          return {
            queryKey,
            queryFn: fetchMerchantOnboardingSteps,
          };
        }
      ),

      skipOnboardingStep: authenticatedQuery(queryClient)(
        (withAuth, params) => {
          const tokenForKey =
            queryClient.getQueryData(keys.authToken) || 'loading';

          const mutationKey = keys.merchantOnboardingsV2.skipOnboardingStep(
            params.merchantId,
            String(tokenForKey)
          );

          async function skipLastMerchantOnboardingStep() {
            return withAuth((token) =>
              apiClient.merchantOnboardingsV2
                .skipOnboardingStep({
                  merchantId: params.merchantId,
                  params: {
                    // For simplicity, we are hardcoding the step to skip.
                    // In the future, we can allow passing the step to skip as a parameter if needed.
                    step_id: 'setup_shore_pay',
                  },
                })
                .then(handleApiResponse)
                .catch(
                  injectErrorMetadata({
                    mutationKey,
                    token,
                    merchantId: params?.merchantId,
                  })
                )
            );
          }

          return {
            mutationKey,
            mutationFn: skipLastMerchantOnboardingStep,
          };
        }
      ),
    },
    merchantPaymentSettingsV2: authenticatedQuery(queryClient)(
      (withAuth, params) => {
        const tokenForKey =
          queryClient.getQueryData(keys.authToken) || 'loading';
        const queryKey = keys.merchantPaymentSettingsV2(
          params.merchantId,
          String(tokenForKey)
        );

        async function fetchMerchantPaymentSettings() {
          return withAuth((token) =>
            apiClient.merchantPaymentSettingsV2
              .getPaymentMethods()
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchMerchantPaymentSettings,
        };
      }
    ),

    merchantPaymentSettingsV2b: authenticatedQuery(queryClient)(
      (withAuth, params) => {
        const tokenForKey =
          queryClient.getQueryData(keys.authToken) || 'loading';
        const queryKey = keys.merchantPaymentSettingsV2b(
          params.merchantId,
          String(tokenForKey)
        );

        async function fetchPaymentSettingsV2b() {
          return withAuth((token) =>
            apiClient.merchantPaymentSettingsV2b
              .getPaymentMethods()
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchPaymentSettingsV2b,
        };
      }
    ),

    contractV2: {
      findAll: authenticatedQuery(queryClient)((withAuth, organizationId) => {
        const tokenForKey =
          queryClient.getQueryData(keys.authToken) || 'loading';
        const queryKey = keys.contractV2.findAll(
          organizationId,
          String(tokenForKey)
        );

        async function fetchContract() {
          return withAuth((token) =>
            apiClient.contractV2
              .findAll({
                filter: { organization: organizationId },
                include: ['packages', 'fees'],
              })
              .catch(injectErrorMetadata({ queryKey, token, organizationId }))
          );
        }

        return {
          queryKey,
          queryFn: fetchContract,
        };
      }),
    },

    paypal: {
      initiateSetup: authenticatedQuery(queryClient)((withAuth, params) => {
        const queryKey = keys.paypal.initiateSetup(params?.merchantId);

        async function createPaypalSetup() {
          return withAuth((token) =>
            apiClient.payPal.signupLink
              .create({ merchant: { id: params?.merchantId } })
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: createPaypalSetup,
        };
      }),
    },

    bookingConfigsV2: {
      find: authenticatedQuery(queryClient)((withAuth, { merchantId }) => {
        const tokenForKey =
          queryClient.getQueryData(keys.authToken) || 'loading';
        const queryKey = keys.bookingConfigsV2(merchantId, String(tokenForKey));

        async function fetchBookingConfigs() {
          return withAuth((token) =>
            apiClient.bookingConfigsV2
              .find(merchantId)
              .catch(injectErrorMetadata({ merchantId, queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchBookingConfigs,
        };
      }),

      update: authenticatedQuery(queryClient)(
        (withAuth, { merchantId, payload }) => {
          const tokenForKey =
            queryClient.getQueryData(keys.authToken) || 'loading';
          const queryKey = keys.bookingConfigsV2(
            merchantId,
            String(tokenForKey)
          );

          async function updateBookingConfigs() {
            return withAuth((token) =>
              apiClient.bookingConfigsV2
                .update(merchantId, payload)
                .catch(injectErrorMetadata({ queryKey, token }))
            );
          }

          return {
            queryKey,
            queryFn: updateBookingConfigs,
          };
        }
      ),
    },
  };
}
